import { useRef, useState } from 'react';
import {
	Button,
	ColorPicker,
	ConfigProvider,
	Flex,
	Form,
	Input,
	InputNumber,
	Popconfirm,
	Select,
	theme,
	Typography,
	Upload,
} from 'antd';
import { LuImagePlus } from 'react-icons/lu';

import BaseText from '@components/typography/BaseText';
import { useThumbnailUrl } from '@hooks/useThumbnailUrl';
import { uploadTaxonomyThumbnail } from '@utils/api';
import { OBJECT_COLOR_PALETTE } from '@utils/constants';
import {
	describeLabelMissing,
	labelsFromSummary,
	pluralize,
	sampleUnitOf,
	trainingRequirements,
	trainingSampleFor,
} from '@routes/userRoutes/objects/objectTree';
import { formatCount } from '@routes/userRoutes/objects/components/objectDetails/objectsUtils';

export const OBJECT_FORM_MODE = Object.freeze({ CREATE: 'create', EDIT: 'edit' });

/** The create form's title; a dialog around the form shows it instead (`showTitle`). */
export const newObjectTitle = parentName => `New Object from “${parentName ?? ''}”`;

const COLOR_PRESETS = [{ label: 'Presets', colors: [...OBJECT_COLOR_PALETTE] }];

// Thumbnails are served to every user of the picker, so only plain raster
// formats the file service stores as-is are allowed. TIFF is out: the service
// treats it as an orthophoto and renames it, so each one overwrites the last.
export const THUMBNAIL_TYPES = Object.freeze({
	'image/jpeg': ['.jpg', '.jpeg'],
	'image/png': ['.png'],
	'image/webp': ['.webp'],
});
export const THUMBNAIL_MAX_BYTES = 5 * 1024 * 1024;
const THUMBNAIL_ACCEPT = Object.values(THUMBNAIL_TYPES).flat().join(',');
const THUMBNAIL_TYPE_ERROR = 'Use a .jpg, .jpeg, .png or .webp image';
const THUMBNAIL_SIZE_ERROR = 'The image must be 5 MB or smaller';
const THUMBNAIL_UPLOAD_ERROR = 'Upload failed';

// Checks the extension as well as the type: browsers report an empty or generic
// type for some files, and a renamed file can carry the wrong one.
const thumbnailProblem = file => {
	const extension = `.${String(file?.name ?? '')
		.split('.')
		.pop()
		.toLowerCase()}`;
	const allowedExtensions = THUMBNAIL_TYPES[file?.type] ?? [];
	if (!allowedExtensions.includes(extension)) return THUMBNAIL_TYPE_ERROR;
	if ((file?.size ?? 0) > THUMBNAIL_MAX_BYTES) return THUMBNAIL_SIZE_ERROR;
	return null;
};

const UNTRAINABLE_LABEL_ERROR = 'Not enough new annotations to train from this label';

const capitalize = text => String(text ?? '').replace(/^./, char => char.toUpperCase());

const toFileList = event => (Array.isArray(event) ? event : (event?.fileList ?? []));

// "8/10", or the bare count until the summary brings the threshold.
const countAgainst = (count, requirement) =>
	requirement ? `${formatCount(count)}/${formatCount(requirement.required)}` : formatCount(count);

// A count is green once it meets its threshold; without one, green unless the label can't train.
const figureType = (requirement, labelError) =>
	(requirement ? requirement.met : !labelError) ? 'success' : 'warning';

// One figure in the line above the Label field: the count stands out, the word does not.
const SampleFigure = ({ value, word, type }) => (
	<Flex align="baseline" gap={6}>
		<Typography.Title level={4} type={type} style={{ margin: 0 }}>
			{value}
		</Typography.Title>
		<BaseText size="xs" type="secondary">
			{word}
		</BaseText>
	</Flex>
);

// An existing thumbnail arrives as its storage key; antd wants a done file entry.
const toInitialFileList = (key, url) =>
	key ? [{ uid: key, name: key.split('/').pop(), status: 'done', url, response: { key } }] : [];

/**
 * Create/edit form for a taxonomy object. It derives everything it shows from
 * the node it is given, so a caller only decides the mode and what to do with
 * the result:
 *
 * - `create`: `object` is the parent the new object is trained from. The title,
 *   the Label choices and the annotation count come from it; fields start empty
 *   except Name, which follows the picked label until the user types their own.
 *   Above the Label field, the picked label's annotations and
 *   tiles stand against the server's thresholds. Submitting checks the fields, then
 *   asks in a Popconfirm before `onSubmit`: creating starts a training run.
 * - `edit`: `object` is the node being edited and fills the fields; the Label
 *   choices and count come from `parent`, the examples a re-train learns from.
 *
 * A picked thumbnail is uploaded straight away through the file service and
 * only its storage key is kept. Save is blocked while an upload is running or
 * after one failed. No API call other than that upload happens here — the
 * caller runs the create or update and passes its pending state as `loading`.
 *
 * `onSubmit` receives `{ name, description, size, trained_label, color, thumbnail }`,
 * where `size` is metres (or `null`), `color` is `#RRGGBB`, `trained_label` is
 * `''` for Default, and `thumbnail` is the storage key or `null`.
 *
 * @param {'create' | 'edit'} [props.mode]
 * @param {object} props.object - the parent (create) or the node being edited (edit)
 * @param {object} [props.parent] - the edited node's parent (edit only)
 * @param {string} [props.projectMode] - a `PROJECT_MODE` value: names tiles as tiles or images
 * @param {boolean} [props.loading] - the caller's mutation is pending
 * @param {boolean} [props.showTitle] - false when a surrounding dialog shows the title
 * @param {(values: object) => void} props.onSubmit
 * @param {() => void} props.onCancel
 */
const ObjectForm = ({
	mode = OBJECT_FORM_MODE.CREATE,
	object,
	parent,
	projectMode,
	loading = false,
	showTitle = true,
	onSubmit,
	onCancel,
}) => {
	const isEdit = mode === OBJECT_FORM_MODE.EDIT;
	const [form] = Form.useForm();
	const { token } = theme.useToken();
	const thumbnailUrl = useThumbnailUrl(isEdit ? object?.thumbnail : null);

	// What the object learns from: the parent in both modes.
	const trainingSource = isEdit ? parent : object;
	const labels = labelsFromSummary(trainingSource?.annotation_labels);
	const { labelOptions, untrainable } = labels;
	const sampleOf = label =>
		trainingSource ? trainingSampleFor(trainingSource, label, labels) : null;
	// A label the server cannot train from can be picked but not submitted. Edit exempts
	// only the stored label, which must still show; switching to another is checked.
	const storedLabel = isEdit ? (object?.trained_label ?? '') : null;
	const isUntrainable = value => untrainable.has(value) && value !== storedLabel;

	const title = isEdit ? (object?.name ?? '') : newObjectTitle(object?.name);
	const submitLabel = isEdit ? 'Save changes' : 'Add and start training';

	const sampleUnit = sampleUnitOf(projectMode);
	// '' is the Default option, backed by the unlabelled examples. Every label can be
	// picked; the counts above the field and the message under it say what one lacks.
	const selectOptions = ['', ...labelOptions].map(value => {
		const invalid = isUntrainable(value);
		const sample = sampleOf(value);
		return {
			value,
			label: value ? capitalize(value) : 'Default',
			invalid,
			reason:
				invalid && sample
					? describeLabelMissing(trainingSource, sample, projectMode)
					: null,
		};
	});

	// Create opens on the first trainable label; if there is none, the error shows at
	// once instead of after submit.
	const firstTrainable = selectOptions.find(option => !option.invalid)?.value ?? '';
	// Backs up the disabled submit button.
	const validateLabel = (_, value) =>
		isUntrainable(value ?? '')
			? Promise.reject(new Error(UNTRAINABLE_LABEL_ERROR))
			: Promise.resolve();

	// Create names the object after the picked label until the user types a name of
	// their own: this is the last name filled in that way.
	const autoName = useRef(isEdit ? null : capitalize(firstTrainable));

	const initialValues = {
		name: isEdit ? (object?.name ?? '') : autoName.current,
		description: isEdit ? (object?.description ?? '') : '',
		size: isEdit ? (object?.object_size ?? null) : null,
		trained_label: isEdit ? storedLabel : firstTrainable,
		color: (isEdit && object?.color) || OBJECT_COLOR_PALETTE[0],
		thumbnail: isEdit ? toInitialFileList(object?.thumbnail, thumbnailUrl) : [],
	};

	// useWatch is undefined until its effect runs, so the first render falls back to the
	// field's initial value rather than Default.
	const selectedLabel = Form.useWatch('trained_label', form) ?? initialValues.trained_label;
	const shownSample = sampleOf(selectedLabel);
	const shownRequirements =
		shownSample &&
		trainingRequirements({
			...trainingSource,
			annotations: shownSample.annotations,
			annotation_tiles: shownSample.tiles,
		});
	// Tiles only once the summary has counted them, or a missing count would read as 0.
	const showTiles = typeof trainingSource?.annotation_tiles === 'number';
	// What the picked label is missing, or the generic error without counts to say it.
	const labelError = isUntrainable(selectedLabel)
		? (selectOptions.find(option => option.value === selectedLabel)?.reason ??
			UNTRAINABLE_LABEL_ERROR)
		: undefined;

	const handleValuesChange = changed => {
		if (isEdit || !('trained_label' in changed)) return;
		const name = form.getFieldValue('name') ?? '';
		if (name.trim() && name !== autoName.current) return;
		autoName.current = capitalize(changed.trained_label);
		form.setFieldValue('name', autoName.current);
	};

	// Create asks before it submits: its button stays the form's submit button, so Enter
	// works as in edit, but a valid submit opens the confirmation rather than `onSubmit`.
	// An antd Popconfirm rather than the app's confirm dialog: the form can sit in an antd
	// Modal, whose focus trap and Esc would otherwise fight a second dialog system on top of it.
	const [confirming, setConfirming] = useState(false);
	const createButton = useRef(null);
	// antd closes the bubble on Esc only from its trigger; once focus is on the bubble's
	// own buttons, close it here and hand focus back to the button that opened it.
	const closeOnEscape = event => {
		if (event.key !== 'Escape') return;
		event.stopPropagation();
		setConfirming(false);
		createButton.current?.focus();
	};
	const confirmCreate = () => {
		setConfirming(false);
		// The values `onFinish` would hand over; the fields show their own errors.
		form.validateFields()
			.then(handleFinish)
			.catch(() => {});
	};
	// The model learns from the chosen label only, as the count above the Label field shows.
	const confirmMessage = `“${form.getFieldValue('name') ?? ''}” will be created under “${object?.name ?? ''}”, and Biodrone will learn it from ${formatCount(shownSample?.annotations ?? 0)} annotated examples.`;

	const handleFinish = ({ thumbnail, color, ...values }) => {
		onSubmit?.({
			...values,
			color: color?.toUpperCase(),
			// The uploaded or pre-existing image's key; nothing when it was removed.
			thumbnail: thumbnail?.[0]?.response?.key ?? null,
		});
	};

	// antd drives the card's progress and error states; this only bridges its
	// callbacks to the uploader and hands the key back as the response.
	const handleUploadRequest = async ({ file, onProgress, onSuccess, onError }) => {
		try {
			const key = await uploadTaxonomyThumbnail(file, percent => onProgress?.({ percent }));
			onSuccess?.({ key });
		} catch (error) {
			// The upload class rejects with the server message as a bare string, and
			// with `undefined` for a 400, so anything without text gets a plain label.
			const message = error instanceof Error ? error.message : error;
			onError?.(
				new Error(typeof message === 'string' && message ? message : THUMBNAIL_UPLOAD_ERROR)
			);
		}
	};

	// Rejected files never enter the list, so the message is shown on the field.
	const [thumbnailError, setThumbnailError] = useState(null);
	const checkThumbnail = file => {
		const problem = thumbnailProblem(file);
		setThumbnailError(problem);
		return problem ? Upload.LIST_IGNORE : true;
	};

	// Block Save while the picked image is still going up, or if it failed.
	const validateThumbnail = (_, fileList) => {
		const status = fileList?.[0]?.status;
		if (status === 'uploading')
			return Promise.reject(new Error('Wait for the upload to finish'));
		if (status === 'error')
			return Promise.reject(new Error('Upload failed, remove the image and try again'));
		return Promise.resolve();
	};

	return (
		// The help and error lines under the fields ("Needs 4 more annotations…") a step
		// smaller than the labels, which keep the body size.
		<ConfigProvider
			theme={{
				components: {
					Form: { fontSize: token.fontSizeSM, labelFontSize: token.fontSize },
				},
			}}>
			<Form
				form={form}
				layout="horizontal"
				labelCol={{ flex: '110px' }}
				labelAlign="left"
				colon={false}
				requiredMark={false}
				initialValues={initialValues}
				onValuesChange={handleValuesChange}
				onFinish={isEdit ? handleFinish : () => setConfirming(true)}>
				<Flex vertical gap={12}>
					{showTitle && <BaseText strong>{title}</BaseText>}

					{/* Annotations and tiles, the server's two requirements, side by side and
				    alike; each against its threshold ("8/10") once the summary brings one. */}
					{shownSample && (
						<Flex align="baseline" gap={16} wrap>
							<SampleFigure
								value={countAgainst(
									shownSample.annotations,
									shownRequirements?.annotations
								)}
								word="annotations"
								type={figureType(shownRequirements?.annotations, labelError)}
							/>
							{showTiles && (
								<SampleFigure
									value={countAgainst(
										shownSample.tiles,
										shownRequirements?.tiles
									)}
									word={pluralize(
										shownRequirements?.tiles?.required ?? shownSample.tiles,
										sampleUnit
									)}
									type={figureType(shownRequirements?.tiles, labelError)}
								/>
							)}
						</Flex>
					)}

					<div>
						<Form.Item
							name="trained_label"
							label="Label"
							rules={[{ validator: validateLabel }]}
							validateStatus={labelError ? 'error' : undefined}
							help={labelError}>
							{/* Inside the field, not on <body>: the Objects details pane is sticky and
						    scrolls itself, so a body-level dropdown drifts while the page scrolls. */}
							<Select
								options={selectOptions}
								getPopupContainer={trigger => trigger.parentElement}
							/>
						</Form.Item>

						<Form.Item
							name="name"
							label="Name"
							rules={[{ required: true, whitespace: true, message: 'Enter a name' }]}>
							<Input placeholder="e.g. Spruce — storm damage" />
						</Form.Item>

						<Form.Item
							name="description"
							label="Description"
							rules={[
								{
									required: true,
									whitespace: true,
									message: 'Enter a description',
								},
							]}>
							<Input.TextArea rows={2} placeholder="What this object covers" />
						</Form.Item>

						<Form.Item name="size" label="Object size">
							<InputNumber
								min={0}
								step={0.1}
								placeholder="e.g. 5"
								addonAfter="m"
								style={{ width: '100%' }}
							/>
						</Form.Item>

						<Form.Item
							name="thumbnail"
							label="Thumbnail"
							valuePropName="fileList"
							getValueFromEvent={toFileList}
							rules={[{ validator: validateThumbnail }]}
							validateStatus={thumbnailError ? 'error' : undefined}
							help={thumbnailError ?? undefined}>
							<Upload
								listType="picture-card"
								maxCount={1}
								accept={THUMBNAIL_ACCEPT}
								beforeUpload={checkThumbnail}
								customRequest={handleUploadRequest}>
								<Flex vertical align="center" gap={4}>
									<LuImagePlus size={18} aria-hidden="true" />
									<BaseText size="xs">Upload image</BaseText>
								</Flex>
							</Upload>
						</Form.Item>

						<Form.Item
							name="color"
							label="Color"
							getValueFromEvent={color => color.toHexString()}>
							<ColorPicker
								format="hex"
								disabledAlpha
								showText
								presets={COLOR_PRESETS}
							/>
						</Form.Item>
					</div>

					<Flex gap={8}>
						<Button onClick={onCancel} block>
							Cancel
						</Button>
						{isEdit ? (
							<Button
								type="primary"
								htmlType="submit"
								loading={loading}
								disabled={Boolean(labelError)}
								block>
								{submitLabel}
							</Button>
						) : (
							<Popconfirm
								open={confirming}
								onOpenChange={open => !open && setConfirming(false)}
								title="Create and train"
								description={confirmMessage}
								okText="Create and train"
								onConfirm={confirmCreate}
								onCancel={() => setConfirming(false)}
								// The message is a sentence; without a cap it runs on one long line.
								// In a Modal the bubble sits beside its container, inside `.ant-modal`,
								// whose `pointer-events: none` it would inherit: no click would land.
								styles={{
									root: { maxWidth: token.screenXS, pointerEvents: 'auto' },
								}}
								okButtonProps={{ onKeyDown: closeOnEscape }}
								cancelButtonProps={{ onKeyDown: closeOnEscape }}
								// Inside a surrounding dialog, so its focus trap includes the bubble;
								// elsewhere on the body, so a narrow column does not clip it.
								getPopupContainer={trigger =>
									trigger.closest('[role="dialog"]') ?? document.body
								}>
								<Button
									ref={createButton}
									type="primary"
									htmlType="submit"
									loading={loading}
									disabled={Boolean(labelError)}
									block>
									{submitLabel}
								</Button>
							</Popconfirm>
						)}
					</Flex>
				</Flex>
			</Form>
		</ConfigProvider>
	);
};

export default ObjectForm;
